// Turns the raw error string stored on a Sage CSV import row (sage_csv_import_rows.error_msg,
// e.g. `Sage invoice create failed: [{"$severity":"error","$dataCode":"RecordInvalid",...}]`)
// into one plain-English sentence for staff. The raw string is always kept by callers (tooltip,
// failures CSV) -- this only decides what is shown first.

interface SageApiIssue {
  message: string
  source: string
}

function parseSageIssues(raw: string): SageApiIssue[] {
  const start = raw.indexOf('[')
  const end = raw.lastIndexOf(']')
  if (start < 0 || end <= start) return []
  try {
    const parsed = JSON.parse(raw.slice(start, end + 1))
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(i => i && typeof i === 'object')
      .map(i => ({ message: String(i.$message ?? ''), source: String(i.$source ?? '') }))
  } catch {
    return []
  }
}

export function explainSageError(raw: string | undefined | null): string {
  const text = (raw || '').trim()
  if (!text) return 'Failed, but no error message was recorded.'

  const issues = parseSageIssues(text)
  if (issues.length === 0) return text

  const has = (re: RegExp) => issues.some(i => re.test(i.message))

  // Sage returns two issues for this case ("is not included in the list" + the sentence below);
  // they describe one problem, so they collapse into one explanation.
  if (has(/must be 'Zero Rated' for Customers outside the EU/i)) {
    return "This customer is outside the EU in Sage, so the VAT rate must be Zero Rated -- Exempt is not allowed. Change the VAT rate and re-enter it."
  }
  if (issues.some(i => i.source === 'exchange_rate' && /not provided/i.test(i.message))) {
    return "This customer is a foreign-currency account in Sage, which cannot be uploaded automatically yet. Please enter this invoice in Sage manually."
  }
  if (has(/EU Goods\/Services/i)) {
    return 'Sage requires an EU Goods/Services type on this line. The invoice was NOT created in Sage.'
  }

  return issues.map(i => (i.source ? `${i.source}: ${i.message}` : i.message)).join('; ')
}
